import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useSubscription } from '@apollo/client';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { ArrowLeft, Edit2, Calendar as CalendarIcon, User } from 'lucide-react';
import { GET_PROJECT, PROJECT_BOARD_CHANGED_SUBSCRIPTION } from '../../graphql/queries';
import { ProjectKanbanBoard } from './ProjectKanbanBoard';
import { ProjectTaskList } from './ProjectTaskList';
import { ProjectMembersTab } from './ProjectMembersTab';
import { ProjectSettingsTab } from './ProjectSettingsTab';
import { ProjectStatusPill } from './ProjectStatusPill';
import { ProjectProgressBar } from './ProjectProgressBar';
import { ProjectDialog } from './ProjectDialog';
import { ProjectStatus, formatDate } from './projectUtils';
import { usePermission } from '../../hooks/usePermission';

interface ProjectDetailLayoutProps {
  projectId: string;
  currentUserId?: string;
}

interface ProjectFull {
  id: string;
  name: string;
  description?: string | null;
  status: ProjectStatus;
  startDate?: string | null;
  plannedEndDate?: string | null;
  leaderId?: string | null;
  createdAt: string;
  updatedAt: string;
  leader?: {
    id: string;
    firstName: string;
    lastName: string;
    email?: string;
  } | null;
  memberCount?: number | null;
  taskCount?: number | null;
  doneTaskCount?: number | null;
  columns?: Array<{
    id: string;
    name: string;
    color?: string | null;
    order: number;
    isTerminal: boolean;
  }> | null;
  members?: Array<{
    id: string;
    employeeId: string;
    joinedAt: string;
    employee?: {
      id: string;
      firstName: string;
      lastName: string;
      email?: string;
    } | null;
  }> | null;
}

const VALID_TABS = ['kanban', 'list', 'members', 'settings'] as const;
type ProjectTab = (typeof VALID_TABS)[number];

const isValidTab = (value: string | undefined): value is ProjectTab =>
  !!value && (VALID_TABS as readonly string[]).includes(value);

export const ProjectDetailLayout: React.FC<ProjectDetailLayoutProps> = ({
  projectId,
  currentUserId,
}) => {
  const navigate = useNavigate();
  const { tab } = useParams<{ tab?: string }>();
  const canManageAll = usePermission('projects.manage_all');
  const canManageOwn = usePermission('projects.manage_own');

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const activeTab: ProjectTab = isValidTab(tab) ? tab : 'kanban';

  useEffect(() => {
    if (tab === undefined || !isValidTab(tab)) {
      navigate(`/projects/${projectId}/kanban`, { replace: true });
    }
  }, [tab, projectId, navigate]);

  const handleTabChange = (next: string) => {
    navigate(`/projects/${projectId}/${next}`);
  };

  const { data, loading, error, refetch } = useQuery<{ project: ProjectFull }>(
    GET_PROJECT,
    {
      variables: { id: projectId },
      fetchPolicy: 'cache-and-network',
    },
  );

  useSubscription(PROJECT_BOARD_CHANGED_SUBSCRIPTION, {
    variables: { projectId },
    onData: () => {
      refetch().catch(() => {});
    },
    onError: (err) => {
      console.debug('Project detail subscription error:', err);
    },
  });

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (error || !data?.project) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <p className="text-red-600 mb-2">
            Nie udało się załadować projektu
          </p>
          <p className="text-sm text-muted-foreground">
            {error?.message || 'Projekt nie istnieje lub brak uprawnień'}
          </p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => navigate('/projects')}
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Wróć do listy projektów
          </Button>
        </CardContent>
      </Card>
    );
  }

  const project = data.project;
  const isLeader = !!currentUserId && project.leaderId === currentUserId;
  const canEdit = canManageAll || (canManageOwn && isLeader);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/projects')}
            className="mt-1"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h2
                className="text-2xl font-bold text-foreground truncate"
                title={project.name}
              >
                {project.name}
              </h2>
              <ProjectStatusPill status={project.status} />
            </div>
            {project.description && (
              <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                {project.description}
              </p>
            )}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground mt-2">
              {project.leader && (
                <span className="flex items-center gap-1">
                  <User className="w-3.5 h-3.5" />
                  {project.leader.firstName} {project.leader.lastName}
                </span>
              )}
              {(project.startDate || project.plannedEndDate) && (
                <span className="flex items-center gap-1">
                  <CalendarIcon className="w-3.5 h-3.5" />
                  {formatDate(project.startDate)} –{' '}
                  {formatDate(project.plannedEndDate)}
                </span>
              )}
              <span>Członków: {project.memberCount ?? 0}</span>
            </div>
            <ProjectProgressBar
              taskCount={project.taskCount ?? 0}
              doneTaskCount={project.doneTaskCount ?? 0}
              size="md"
              className="mt-3 max-w-md"
            />
          </div>
        </div>

        <div className="flex gap-2 shrink-0">
          {canEdit && (
            <Button
              variant="outline"
              onClick={() => setEditDialogOpen(true)}
            >
              <Edit2 className="w-4 h-4 mr-1" />
              Edytuj
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="grid grid-cols-2 md:flex md:w-auto md:inline-flex">
          <TabsTrigger value="kanban">Kanban</TabsTrigger>
          <TabsTrigger value="list">Lista zadań</TabsTrigger>
          <TabsTrigger value="members">Członkowie</TabsTrigger>
          <TabsTrigger value="settings">Ustawienia</TabsTrigger>
        </TabsList>

        <TabsContent value="kanban" className="mt-4">
          <ProjectKanbanBoard
            projectId={projectId}
            currentUserId={currentUserId}
            canManageProject={canEdit}
          />
        </TabsContent>

        <TabsContent value="list" className="mt-4">
          <ProjectTaskList projectId={projectId} />
        </TabsContent>

        <TabsContent value="members" className="mt-4">
          <ProjectMembersTab
            project={project}
            currentUserId={currentUserId}
          />
        </TabsContent>

        <TabsContent value="settings" className="mt-4">
          <ProjectSettingsTab
            project={project}
            currentUserId={currentUserId}
          />
        </TabsContent>
      </Tabs>

      <ProjectDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        project={{
          id: project.id,
          name: project.name,
          description: project.description,
          status: project.status,
          startDate: project.startDate,
          plannedEndDate: project.plannedEndDate,
          leaderId: project.leaderId,
          leader: project.leader,
        }}
      />
    </div>
  );
};
